import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const GIG_ROLES = [
  'Photographer',
  'Videographer',
  'DJ / Music',
  'Sound and AV Technical',
  'Lighting Technical',
  'Stage and Production Crew',
  'Art Installation and Handling',
  'Front of House and Ticketing',
  'Hospitality and Guest Management',
  'Food and Beverage Service',
  'General Event Crew and Volunteering',
  'Driver and Logistics',
  'Translation and Interpretation',
  'Social Media and Content at Events',
  'Hair, Makeup and Styling',
  'Security and Crowd Management',
  'Other (please specify)'
];

const EVENT_COUNT_OPTIONS = [
  'This would be my first',
  '1 to 5 events',
  '5 to 20 events',
  'More than 20 events'
];

const NOTICE_OPTIONS = ['Same day', '24 hours', '48 hours', 'One week or more'];

const EMPLOYMENT_OPTIONS = [
  'Employed full time',
  'Employed part time',
  'Freelance / self employed',
  'Not currently employed / studying'
];

const DAY_OPTIONS = ['Weekdays', 'Weekends', 'Both'];

const YN_S = ['Yes', 'No', 'Sometimes'];
const YN = ['Yes', 'No'];

const SECTIONS = [
  { id: 'basics', label: 'Basics' },
  { id: 'work', label: 'What you do' },
  { id: 'availability', label: 'Availability' },
  { id: 'about', label: 'About you' },
  { id: 'consent', label: 'Consent' }
];

/* ------------------------------------------------------------------ */
/* Small subcomponents                                                  */
/* ------------------------------------------------------------------ */

const SectionLabel = ({ children }) => (
  <p
    className="text-xs uppercase tracking-[0.25em] font-semibold mb-6"
    style={{ color: '#D4820A' }}
  >
    {children}
  </p>
);

const Label = ({ children, required, hint }) => (
  <label className="text-sm font-medium mb-2 block" style={{ color: '#0F2942' }}>
    {children}{required && <span style={{ color: '#D4820A' }}> *</span>}
    {hint && (
      <span className="block text-xs font-normal mt-1" style={{ color: '#0F2942' }}>
        {hint}
      </span>
    )}
  </label>
);

const RadioGroup = ({ name, value, onChange, options, testid }) => (
  <div className="flex flex-wrap gap-2.5" data-testid={testid}>
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className="px-4 py-2 rounded-full text-sm transition-all"
          style={{
            backgroundColor: selected ? '#0F2942' : '#FFFFFF',
            color: selected ? '#FFFFFF' : '#0F2942',
            border: selected ? '1.5px solid #0F2942' : '1.5px solid #F0EBE2'
          }}
          data-testid={`${testid}-${opt.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */

const initialForm = {
  full_name: '',
  phone: '',
  email: '',
  city: '',
  travels: '',
  travel_cities: '',
  gig_roles: [],
  gig_roles_other: '',
  events_worked: '',
  cultural_experience: '',
  cultural_example: '',
  short_notice: '',
  notice_period: '',
  days_available: [],
  employment_status: '',
  about_strength: '',
  instagram_handle: ''
};

const CrewNetworkPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Photo upload
  const [photoBase64, setPhotoBase64] = useState(null);
  const [photoFilename, setPhotoFilename] = useState('');
  const [photoError, setPhotoError] = useState('');

  // Consent
  const [consentContact, setConsentContact] = useState(false);
  const [consentNoGuarantee, setConsentNoGuarantee] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);

  const [activeSection, setActiveSection] = useState('basics');

  /* track scroll position to highlight active section in progress bar */
  useEffect(() => {
    const onScroll = () => {
      const offsets = SECTIONS.map((s) => {
        const el = document.getElementById(`section-${s.id}`);
        if (!el) return { id: s.id, top: Infinity };
        return { id: s.id, top: Math.abs(el.getBoundingClientRect().top - 100) };
      });
      offsets.sort((a, b) => a.top - b.top);
      setActiveSection(offsets[0].id);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArr = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v]
    }));

  const showTravelCities = form.travels === 'Yes' || form.travels === 'Sometimes';
  const showCulturalExample = form.cultural_experience === 'Yes';
  const showRoleOther = form.gig_roles.includes('Other (please specify)');

  const handlePhoto = (e) => {
    setPhotoError('');
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setPhotoError('Photo must be 3MB or smaller.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please upload an image file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setPhotoBase64(evt.target.result);
      setPhotoFilename(file.name);
    };
    reader.readAsDataURL(file);
  };

  const canSubmit = useMemo(() => {
    return (
      form.full_name &&
      form.phone &&
      form.email &&
      form.city &&
      form.travels &&
      form.gig_roles.length > 0 &&
      form.events_worked &&
      form.cultural_experience &&
      form.short_notice &&
      form.notice_period &&
      form.days_available.length > 0 &&
      form.employment_status &&
      photoBase64 &&
      consentContact &&
      consentNoGuarantee &&
      consentTerms
    );
  }, [form, photoBase64, consentContact, consentNoGuarantee, consentTerms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please complete all required fields, upload a photo, and tick the three consent boxes.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        gig_roles_other: showRoleOther ? form.gig_roles_other : null,
        travel_cities: showTravelCities ? form.travel_cities : null,
        cultural_example: showCulturalExample ? form.cultural_example : null,
        profile_photo_base64: photoBase64 || null,
        profile_photo_filename: photoFilename || null,
        consent_contact: consentContact,
        consent_no_guarantee: consentNoGuarantee,
        consent_terms: consentTerms,
        email_opt_in: emailOptIn
      };
      await axios.post(`${API_URL}/api/crew`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ------------------------------------ Success state ------------------------------------ */
  if (submitted) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
        <Navbar />
        <div className="px-6 md:px-12 lg:px-24 py-32">
          <div className="max-w-xl mx-auto text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: 'rgba(212,130,10,0.15)' }}
            >
              <svg className="w-7 h-7" style={{ color: '#D4820A' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
            >
              You&rsquo;re In
            </h1>
            <p className="text-base mb-2" style={{ color: '#0F2942' }}>
              Thank you for joining the CultureCareers Event and Festival Crew Network.
            </p>
            <p className="text-sm" style={{ color: '#0F2942' }}>
              We will reach out via WhatsApp when a relevant opportunity comes up.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  /* ------------------------------------ Form ------------------------------------ */
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />
      <SEO
        title="Event & Festival Crew Network"
        path="/talent/crew"
        description="Register with CultureCareers Event and Festival Crew Network. Photographers, sound, stage, hospitality, security, drivers and more. India's leading cultural events and festivals find crew through us."
      />

      {/* Hero */}
      <section
        className="px-6 md:px-12 lg:px-24 py-16 md:py-24"
        style={{ backgroundColor: '#0F2942' }}
      >
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: '#D4820A' }}>
            CultureCareers Crew Network
          </p>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight mb-6 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Event &amp; Festival{' '}
            <span style={{ color: '#D4820A', fontStyle: 'italic' }}>Crew</span> Network
          </h1>
          <div className="w-20 h-px mb-8" style={{ backgroundColor: 'rgba(212,130,10,0.6)' }} />
          <p
            className="text-base md:text-lg leading-relaxed font-light max-w-2xl"
            style={{ color: 'rgba(240, 235, 226, 0.72)' }}
          >
            CultureCareers connects event and festival crew with India&rsquo;s best cultural events, art fairs, festivals, and creative productions. Register below and we will reach out when the right opportunity comes up. Takes two minutes.
          </p>
        </div>
      </section>

      {/* Sticky progress bar */}
      <div
        className="sticky top-0 z-30 backdrop-blur-sm"
        style={{ backgroundColor: 'rgba(247,245,242,0.95)', borderBottom: '1px solid #F0EBE2' }}
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-3 flex items-center gap-2 overflow-x-auto">
          {SECTIONS.map((s, i) => {
            const isActive = s.id === activeSection;
            return (
              <a
                key={s.id}
                href={`#section-${s.id}`}
                className="flex items-center gap-2 text-xs font-medium whitespace-nowrap transition-colors px-2 py-1 rounded"
                style={{ color: isActive ? '#D4820A' : '#0F2942' }}
                data-testid={`crew-progress-${s.id}`}
              >
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold"
                  style={{
                    backgroundColor: isActive ? '#D4820A' : '#F0EBE2',
                    color: isActive ? '#FFFFFF' : '#0F2942'
                  }}
                >
                  {i + 1}
                </span>
                {s.label}
              </a>
            );
          })}
        </div>
      </div>

      {/* Form */}
      <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16">
        <form
          onSubmit={handleSubmit}
          className="max-w-3xl mx-auto space-y-12 bg-white rounded-xl p-6 md:p-10 shadow-sm"
          data-testid="crew-form"
        >
          {/* ============ Section 1: Basics ============ */}
          <div id="section-basics" className="scroll-mt-24">
            <SectionLabel>Basics</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label required>Full Name</Label>
                <Input
                  required
                  value={form.full_name}
                  onChange={(e) => setField('full_name', e.target.value)}
                  className="rounded-lg"
                  placeholder="Your full name"
                  data-testid="crew-name"
                />
              </div>
              <div className="md:col-span-2">
                <Label required hint="We will contact you via WhatsApp.">Phone Number</Label>
                <Input
                  required
                  type="tel"
                  inputMode="tel"
                  pattern="[0-9+\-\s]*"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  className="rounded-lg"
                  placeholder="+91 98765 43210"
                  data-testid="crew-phone"
                />
              </div>
              <div>
                <Label required>Email</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  className="rounded-lg"
                  placeholder="you@example.com"
                  data-testid="crew-email"
                />
              </div>
              <div>
                <Label required>City</Label>
                <Input
                  required
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                  className="rounded-lg"
                  placeholder="e.g. Mumbai"
                  data-testid="crew-city"
                />
              </div>
              <div className="md:col-span-2">
                <Label required>Open to travelling for work?</Label>
                <RadioGroup
                  testid="crew-travels"
                  name="travels"
                  value={form.travels}
                  onChange={(v) => setField('travels', v)}
                  options={YN_S}
                />
              </div>
              {showTravelCities && (
                <div className="md:col-span-2">
                  <Label hint="Optional. Helps us match you to the right gigs.">
                    Which cities are you happy to work in?
                  </Label>
                  <Input
                    value={form.travel_cities}
                    onChange={(e) => setField('travel_cities', e.target.value)}
                    className="rounded-lg"
                    placeholder="e.g. Delhi, Goa, Bangalore"
                    data-testid="crew-travel-cities"
                  />
                </div>
              )}
            </div>
          </div>

          {/* ============ Section 2: What you do ============ */}
          <div id="section-work" className="scroll-mt-24">
            <SectionLabel>What you do</SectionLabel>
            <div className="space-y-6">
              <div>
                <Label required hint="Select all that apply.">Gig roles you are available for</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mt-2">
                  {GIG_ROLES.map((r) => {
                    const checked = form.gig_roles.includes(r);
                    return (
                      <label
                        key={r}
                        className="flex items-start gap-3 cursor-pointer p-3 rounded-lg transition-colors"
                        style={{
                          backgroundColor: checked ? 'rgba(212,130,10,0.15)' : '#F0EBE2',
                          border: `1px solid ${checked ? 'rgba(212,130,10,0.5)' : '#F0EBE2'}`
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleArr('gig_roles', r)}
                          data-testid={`crew-role-${r.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                          className="mt-0.5"
                        />
                        <span className="text-sm" style={{ color: '#0F2942' }}>{r}</span>
                      </label>
                    );
                  })}
                </div>
                {showRoleOther && (
                  <div className="mt-3">
                    <Input
                      value={form.gig_roles_other}
                      onChange={(e) => setField('gig_roles_other', e.target.value)}
                      className="rounded-lg"
                      placeholder="Tell us what role"
                      data-testid="crew-role-other"
                    />
                  </div>
                )}
              </div>

              <div>
                <Label required>How many events have you worked?</Label>
                <RadioGroup
                  testid="crew-events-worked"
                  value={form.events_worked}
                  onChange={(v) => setField('events_worked', v)}
                  options={EVENT_COUNT_OPTIONS}
                />
              </div>

              <div>
                <Label required>
                  Do you have experience at cultural events specifically: festivals, art fairs, museums, or theatre?
                </Label>
                <RadioGroup
                  testid="crew-cultural-exp"
                  value={form.cultural_experience}
                  onChange={(v) => setField('cultural_experience', v)}
                  options={YN}
                />
              </div>
              {showCulturalExample && (
                <div>
                  <Label hint="Optional. Briefly name one.">If yes, briefly name one</Label>
                  <Input
                    value={form.cultural_example}
                    onChange={(e) => setField('cultural_example', e.target.value)}
                    className="rounded-lg"
                    placeholder="e.g. Kochi Biennale 2024"
                    data-testid="crew-cultural-example"
                  />
                </div>
              )}
            </div>
          </div>

          {/* ============ Section 3: Availability ============ */}
          <div id="section-availability" className="scroll-mt-24">
            <SectionLabel>Availability</SectionLabel>
            <div className="space-y-6">
              <div>
                <Label required>Available on short notice?</Label>
                <RadioGroup
                  testid="crew-short-notice"
                  value={form.short_notice}
                  onChange={(v) => setField('short_notice', v)}
                  options={YN_S}
                />
              </div>
              <div>
                <Label required>Preferred notice period</Label>
                <RadioGroup
                  testid="crew-notice-period"
                  value={form.notice_period}
                  onChange={(v) => setField('notice_period', v)}
                  options={NOTICE_OPTIONS}
                />
              </div>
              <div>
                <Label required>Days available</Label>
                <div className="flex flex-wrap gap-2.5">
                  {DAY_OPTIONS.map((d) => {
                    const checked = form.days_available.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleArr('days_available', d)}
                        className="px-4 py-2 rounded-full text-sm transition-all"
                        style={{
                          backgroundColor: checked ? '#0F2942' : '#FFFFFF',
                          color: checked ? '#FFFFFF' : '#0F2942',
                          border: `1.5px solid ${checked ? '#0F2942' : '#F0EBE2'}`
                        }}
                        data-testid={`crew-days-${d.toLowerCase()}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label required>Current employment status</Label>
                <RadioGroup
                  testid="crew-employment"
                  value={form.employment_status}
                  onChange={(v) => setField('employment_status', v)}
                  options={EMPLOYMENT_OPTIONS}
                />
              </div>
            </div>
          </div>

          {/* ============ Section 4: About you ============ */}
          <div id="section-about" className="scroll-mt-24">
            <SectionLabel>About you</SectionLabel>
            <div className="space-y-6">
              <div>
                <Label hint="Optional. Max 150 characters.">Tell us one thing that makes you good at this</Label>
                <Textarea
                  value={form.about_strength}
                  onChange={(e) => setField('about_strength', e.target.value.slice(0, 150))}
                  rows={3}
                  className="rounded-lg"
                  placeholder="e.g. I have worked three editions of NH7 Weekender and I always show up early."
                  data-testid="crew-about"
                />
                <p className="text-xs mt-1.5 text-right" style={{ color: '#0F2942' }}>
                  {form.about_strength.length}/150
                </p>
              </div>

              <div>
                <Label hint="Optional. Especially useful for photographers, videographers, and stylists. This is your portfolio.">
                  Instagram handle
                </Label>
                <Input
                  value={form.instagram_handle}
                  onChange={(e) => setField('instagram_handle', e.target.value)}
                  className="rounded-lg"
                  placeholder="@yourhandle"
                  data-testid="crew-instagram"
                />
              </div>

              <div>
                <Label required hint="A photo that shows your personality. Max 3MB.">
                  Profile photo
                </Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhoto}
                  className="block w-full text-sm cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#0F2942] file:text-white hover:file:opacity-90"
                  data-testid="crew-photo"
                />
                {photoFilename && !photoError && (
                  <p className="text-xs mt-2" style={{ color: '#0F2942' }}>
                    Selected: {photoFilename}
                  </p>
                )}
                {photoError && (
                  <p className="text-xs mt-2" style={{ color: '#D4820A' }}>{photoError}</p>
                )}
              </div>
            </div>
          </div>

          {/* ============ Section 5: Consent ============ */}
          <div id="section-consent" className="scroll-mt-24">
            <SectionLabel>Consent</SectionLabel>
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={consentContact} onCheckedChange={setConsentContact} className="mt-0.5" data-testid="crew-consent-contact" />
                <span className="text-sm leading-relaxed" style={{ color: '#0F2942' }}>
                  I agree to be contacted by CultureCareers for relevant gig and event opportunities. <span style={{ color: '#D4820A' }}>*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={consentNoGuarantee} onCheckedChange={setConsentNoGuarantee} className="mt-0.5" data-testid="crew-consent-no-guarantee" />
                <span className="text-sm leading-relaxed" style={{ color: '#0F2942' }}>
                  I understand that registering does not guarantee placement. <span style={{ color: '#D4820A' }}>*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={consentTerms} onCheckedChange={setConsentTerms} className="mt-0.5" data-testid="crew-consent-terms" />
                <span className="text-sm leading-relaxed" style={{ color: '#0F2942' }}>
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#D4820A' }}>
                    Terms and Conditions
                  </Link>
                  . <span style={{ color: '#D4820A' }}>*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={emailOptIn} onCheckedChange={setEmailOptIn} className="mt-0.5" data-testid="crew-opt-in" />
                <span className="text-sm leading-relaxed" style={{ color: '#0F2942' }}>
                  I would like to receive updates and opportunities from CultureCareers.
                </span>
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4 border-t" style={{ borderColor: '#F0EBE2' }}>
            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="w-full md:w-auto px-8 py-6 rounded-lg text-base font-medium"
              style={{
                backgroundColor: canSubmit ? '#D4820A' : 'rgba(212,130,10,0.45)',
                color: '#FFFFFF'
              }}
              data-testid="crew-submit"
            >
              {submitting ? 'Submitting...' : 'Join the Crew Network'}
            </Button>
            {!canSubmit && (
              <p className="text-xs mt-3" style={{ color: '#0F2942' }}>
                Please complete all required fields, upload your photo, and tick the three required consent boxes.
              </p>
            )}
          </div>
        </form>
      </section>

      {/* Why Join anchor section */}
      <section id="why-join" className="px-6 md:px-12 lg:px-24 py-20 md:py-24" style={{ backgroundColor: '#F0EBE2' }} data-testid="why-join-anchor">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: '#D4820A' }}>
            Why Join?
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif", color: '#0F2942' }}
          >
            When India&rsquo;s best events need crew, they come to us.
          </h2>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: '#0F2942' }}>
            CultureCareers works with India&rsquo;s leading festivals, galleries, museums, and cultural organisations. When they need people for a permanent role, a freelance project, or a single day of work, they come to us. Being on our network means they can find you.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CrewNetworkPage;
